import { FC, useLayoutEffect, useRef } from 'react';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import Cookies from 'js-cookie';
import './Charts.scss';

type BarChartProps = {
  name: string;
  data: { name: string; value: number }[];
  chartHeight?: number;
};

const BarChart: FC<BarChartProps> = ({ name, data, chartHeight = 500 }) => {
  const chartId = `${name}-${Date.now()}`;
  const seriesRef = useRef<any>(null);

  useLayoutEffect(() => {
    am5.ready(() => {
      const root = am5.Root.new(chartId);
      root.setThemes([am5themes_Animated.new(root)]);

      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          layout: root.verticalLayout,
          panX: false,
          panY: false,
          wheelX: 'none',
          wheelY: 'none',
        })
      );

      // Y Axis (Categories)
      const yRenderer = am5xy.AxisRendererY.new(root, {
        minGridDistance: 20,
        inversed: true,
      });

      yRenderer.labels.template.setAll({
        oversizedBehavior: 'wrap',
        maxWidth: 200,
        fontSize: Number(Cookies.get('globalFontSize')) * 0.85 || 12,
        fill: Cookies.get('darkMode') === 'true' ? am5.color(0xd9d9d9) : am5.color(0x003b5c),
        textAlign: "right"
      });

      const yAxis = chart.yAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: 'name',
          renderer: yRenderer,
        })
      );
      yAxis.data.setAll(data);

      // X Axis (Values)
      const xRenderer = am5xy.AxisRendererX.new(root, {
        minGridDistance: 30,
      });

      const xAxis = chart.xAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: xRenderer,
        })
      );

      xRenderer.labels.template.setAll({
        fontSize: Number(Cookies.get('globalFontSize')) * 0.85 || 12,
        fill: Cookies.get('darkMode') === 'true' ? am5.color(0xd9d9d9) : am5.color(0x003b5c),

      });

xRenderer.labels.template.adapters.add("text", (text, target) => {
  const rawValue = (target.dataItem as any)?.get("value");

  if (typeof rawValue === "number") {
    if (rawValue >= 1_000_000_000) return (rawValue / 1_000_000_000).toFixed(1) + "B";
    if (rawValue >= 1_000_000) return (rawValue / 1_000_000).toFixed(1) + "M";
    if (rawValue >= 1_000) return (rawValue / 1_000).toFixed(0) + "K";
    return rawValue.toString();
  }

  return text;
});


      seriesRef.current = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: 'Employment',
          xAxis: xAxis,
          yAxis: yAxis,
          valueXField: 'value',
          categoryYField: 'name',
          tooltip: am5.Tooltip.new(root, {
            labelText: '{name}: [bold]{valueX}[/]',
          }),
        })
      );

      seriesRef.current.columns.template.setAll({
        fill: Cookies.get('darkMode') === 'true' ? am5.color(0xd9d9d9) : am5.color(0x003b5c),
        stroke: am5.color(0xffffff),
        cornerRadiusTR: 4,
        cornerRadiusBR: 4,
        height: 25,
      });

      seriesRef.current.data.setAll(data);

      chart.set('cursor', am5xy.XYCursor.new(root, {}));
      seriesRef.current.appear(1000);
      chart.appear(1000, 100);
    });
  }, []);

  return <div id={chartId} className="chart-container" style={{ height: chartHeight }} />;
};

export default BarChart;
